import { Link } from "react-router-dom";

type Props = {
  reason?: string;
  courseName?: string;
  factorId?: string;
  createdAt?: string;
  purchaseSource?: string;
};

/**
 * SnappPay-only state: the installment payment could not be confirmed yet (e.g.
 * a lost settle response). We must NOT tell the user it failed and must NOT
 * grant access — it gets reconciled server-side. Just reassure them.
 */
export default function PendingPayment({
  courseName,
  factorId,
  createdAt,
  purchaseSource,
}: Props) {
  const hasReceiptData = Boolean(factorId || createdAt || purchaseSource);

  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-default-100 bg-background shadow-lg shadow-default-900/5 print:mx-auto">
      <div className="relative flex flex-col items-center gap-3 px-4 pt-6 pb-4">
        <span className="flex size-16 items-center justify-center rounded-full bg-warning/10">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            className="text-warning"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M12 7.5V12l3 2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <p className="text-base font-bold text-default-900">
          در حال نهایی‌سازی پرداخت
        </p>
      </div>

      <div className="relative h-3">
        <span className="absolute -right-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <span className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 border-t border-dashed border-default-200" />
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5 pt-3">
        <div className="rounded-xl bg-warning/5 border border-warning/20 p-4 text-center text-sm leading-7 text-warning-700">
          پرداخت اقساطی شما در حال نهایی‌سازی است؛ نتیجه به‌زودی اعلام می‌شود.
        </div>

        {courseName ? (
          <p className="text-center text-sm leading-7 text-default-700">
            دوره{" "}
            <span className="font-bold text-default-900">{courseName}</span>
          </p>
        ) : null}

        {hasReceiptData ? (
          <div className="flex flex-col gap-6 rounded-xl bg-content3/40 p-4 text-xs">
            {factorId ? (
              <ReceiptRow label="شماره پیگیری" value={factorId} mono />
            ) : null}
            {createdAt ? <ReceiptRow label="تاریخ" value={createdAt} /> : null}
            {purchaseSource ? (
              <ReceiptRow label="خرید از طریق" value={purchaseSource} />
            ) : null}
          </div>
        ) : null}

        <Link
          replace={true}
          className="block w-full rounded-xl bg-primary p-3.5 text-center text-sm font-bold text-white shadow-md shadow-primary/30 print:hidden"
          to="/my-courses"
        >
          مشاهده تراکنش‌ها
        </Link>
      </div>
    </div>
  );
}

function ReceiptRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex w-full items-center gap-2">
      <span className="shrink-0 text-default-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-default-300" />
      <span
        className={`shrink-0 font-semibold text-default-800 ${mono ? "font-mono tracking-tight" : ""}`}
        dir={mono ? "ltr" : undefined}
      >
        {value || "-"}
      </span>
    </div>
  );
}
